import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigurationService } from 'src/app/core/config/configuration.service';
import { HttpParamsFromListRequestDTO, ListRequestDTO, ListResponseDTO } from '../';
import { GroupSummaryDTO } from '../groups/group.proxy';

@Injectable()
export class PracticeProxy {
    constructor(
        private http: HttpClient,
        private configurationService: ConfigurationService,
    ) {}

    async get(
        options: ListRequestDTO & {
            includeStatistics: boolean;
            groupId?: string;
        },
    ): Promise<ListResponseDTO<PracticeSummaryDTO>> {
        const url = this.configurationService.APIURL + '/api/practices';
        let params = HttpParamsFromListRequestDTO(options);
        params = params.append('includeStatistics', options.includeStatistics);
        if (options.groupId) {
            params = params.append('groupId', options.groupId);
        }
        return await this.http.get<ListResponseDTO<PracticeSummaryDTO>>(url, { params: params }).toPromise();
    }

    async getPractice(id: string): Promise<PracticeDTO> {
        const url = this.configurationService.APIURL + '/api/practices/' + id;
        return await this.http.get<PracticeDTO>(url).toPromise();
    }

    async getPracticeDetails(id: string): Promise<PracticeDetailsDTO> {
        const url = this.configurationService.APIURL + '/api/practices/' + id + '/details';
        return await this.http.get<PracticeDetailsDTO>(url).toPromise();
    }

    async getPracticeContact(id: string): Promise<PracticeContactDTO> {
        const url = this.configurationService.APIURL + '/api/practices/' + id + '/contact';
        return await this.http.get<PracticeDetailsDTO>(url).toPromise();
    }

    async updatePracticeContact(id: string, practiceContactDTO: PracticeContactDTO): Promise<object> {
        const url = this.configurationService.APIURL + '/api/practices/' + id + '/contact';
        return await this.http.put<PracticeDetailsDTO>(url, practiceContactDTO).toPromise();
    }

    async getPracticeConfiguration(id: string): Promise<PracticeConfigurationDTO> {
        const url = this.configurationService.APIURL + '/api/practices/' + id + '/configuration';
        return await this.http.get<PracticeConfigurationDTO>(url).toPromise();
    }

    async updatePracticeConfiguration(id: string, practiceConfigurationDTO: PracticeConfigurationDTO): Promise<object> {
        const url = this.configurationService.APIURL + '/api/practices/' + id + '/configuration';
        return await this.http.put<PracticeDetailsDTO>(url, practiceConfigurationDTO).toPromise();
    }

    async updatePracticeNotes(id: string, practiceNotesDTO: PracticeNotesDTO): Promise<object> {
        const url = this.configurationService.APIURL + '/api/practices/' + id + '/notes';
        return await this.http.put<PracticeDetailsDTO>(url, practiceNotesDTO).toPromise();
    }

    async practiceNameExists(name: string): Promise<boolean> {
        const url = this.configurationService.APIURL + '/api/practices/any/name';
        let params = new HttpParams();
        params = params.append('name', name);
        return await this.http.get<boolean>(url, { params: params }).toPromise();
    }

    async debtorNumberExists(debtorNumber: string): Promise<boolean> {
        const url = this.configurationService.APIURL + '/api/practices/any/debtorNumber';
        let params = new HttpParams();
        params = params.append('debtorNumber', debtorNumber);
        return await this.http.get<boolean>(url, { params: params }).toPromise();
    }

    async export(): Promise<Blob> {
        const url = this.configurationService.APIURL + '/api/practices/export';
        return await this.http.get(url, { responseType: 'blob' }).toPromise();
    }

    async getQualityConfiguration(groupId: string, practiceId: string): Promise<PracticeQualityConfigurationDTO> {
        const url = this.configurationService.APIURL + `/api/groups/${groupId}/practices/${practiceId}/quality-configuration`;
        return await this.http.get<PracticeQualityConfigurationDTO>(url).toPromise();
    }

    async updateQualityConfiguration(groupId: string, practiceId: string, dto: PracticeQualityConfigurationDTO): Promise<void> {
        const url = this.configurationService.APIURL + `/api/groups/${groupId}/practices/${practiceId}/quality-configuration`;
        await this.http.put(url, dto).toPromise();
    }
}

export interface PracticeDTO {
    createdOn: Date | string;
    name: string;
    debtorNumber: string;
    countryCode: string;
    language: string;
    invoiceParentPracticeName: string;
    purgeInterval: PurgeIntervals;
    isStockInitialized: boolean;
    isChairOrdersEnabled: boolean;
}

export interface PracticeSummaryDTO {
    id: string;
    createdOn: Date | string;

    name: string;
    debtorNumber: string;
    countryCode: string;
    invoiceParentPracticeName: string;
    groupName: string;

    nbOrdersInLastYear: number;
    nbInvoicesInLastYear: number;
    nbAutoReplenishProducts: number;
    nbChairOrdersInLastYear: number;
    nbChairs: number;
}

export interface PracticeDetailsDTO {
    id: string;
    createdOn: Date | string;
    name: string;
    debtorNumber: string;

    street: string;
    number: string;
    postalCode: string;
    city: string;
    countryCode: string;

    mainContactEmail: string;

    language: string;
    purgeInterval: PurgeIntervals;
    focusArea: string;
    groupName: string;
    hasInvoiceParent: boolean;
    hasInvoiceNotificationAttachment: boolean;
    isChairOrdersEnabled: boolean;
    chairOrderIntervalStart: Date | string | null;
    invoiceParentPracticeName: string;
    invoiceInterval: InvoiceIntervals;

    notes: string;
    phoneNumber: string;
    users: PracticeDetailsUserDTO[];
}

export interface PracticeDetailsUserDTO {
    firstName: string;
    lastName: string;
    email: string;
    isMainContact: boolean;
    isPracticeManager: boolean;
    isInvoiceReceiver: boolean;
    isResultReceiver: boolean;
}

export interface PracticeContactDTO {
    phoneNumber: string;

    street: string;
    number: string;
    postalCode: string;
    city: string;
    countryCode: string;
}

export interface PracticeConfigurationDTO {
    id: string;
    purgeInterval: PurgeIntervals;
    focusArea: string;
    language: string;
    hasInvoiceNotificationAttachment: boolean;
    // admin only
    name: string;
    debtorNumber: string;
    invoiceParentPracticeId: string;
    invoiceParentPractice: PracticeSummaryDTO;
    invoiceInterval: InvoiceIntervals;
    isChairOrdersEnabled: boolean;
    chairOrderIntervalStart: Date | string | null;
    // admin only
    groupId: string;
    group: GroupSummaryDTO;
}

export interface PracticeNotesDTO {
    notes: string;
}

export interface PracticeQualityConfigurationDTO {
    id: string;
    name: string;
    chairOrderActionReminderInterval: ActionReminderIntervals;
}

export enum PurgeIntervals {
    weekly = 0,
    monthly = 1,
    yearly = 2,
    never = 3,
}

export enum InvoiceIntervals {
    instant = 0,
    daily = 1,
    weekly = 2,
    monthly = 3,
    never = 4,
}

export enum ActionReminderIntervals {
    weekly = 2,
    monthly = 3,
}
